import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Linkedin, ArrowRight, Loader2, Check, AlertTriangle, RefreshCw } from "lucide-react";
import ChapterHeader from "./ChapterHeader";
import { CONTENT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", message: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required.";
  if (!form.email.trim()) errors.email = "Email is required.";
  else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address.";
  if (!form.message.trim()) errors.message = "Message can't be empty.";
  return errors;
}

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState("");

  const change = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    setForm(next);
    if (touched[k]) setErrors(validate(next));
  };
  const blur = (k) => () => {
    setTouched((t) => ({ ...t, [k]: true }));
    setErrors(validate(form));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    setTouched({ name: true, email: true, message: true });
    if (Object.keys(errs).length) return;
    if (form.website) {
      // Honeypot filled — silently succeed for bots.
      setStatus("success");
      return;
    }
    setStatus("submitting");
    setServerError("");
    try {
      const { website, ...payload } = form;
      await axios.post(`${API}/contact`, payload);
      setStatus("success");
      toast.success("Message delivered. I’ll reply within 24 hours.");
      setForm(initial);
      setTouched({});
    } catch (err) {
      const detail = err?.response?.data?.detail || "Delivery failed. Please retry or email me directly.";
      setServerError(typeof detail === "string" ? detail : "Delivery failed. Please retry or email me directly.");
      setStatus("error");
      toast.error("Message not sent.");
    }
  };

  const submitting = status === "submitting";

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-border py-24 md:py-40 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <ChapterHeader n="07" kicker="Let&rsquo;s talk" title={<>Get in<br /><span className="text-accent">touch</span>.</>}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5 space-y-8"
            >
              <p className="text-lg md:text-xl leading-relaxed text-foreground/85 max-w-[42ch]" data-testid="contact-intro">
                For senior cloud and security work, technical collaboration, or a conversation about multi-cloud IAM and identity
                governance — reach out.
              </p>

              <div className="space-y-4">
                <a
                  data-testid="contact-info-email"
                  href={`mailto:${CONTENT.contact.email}`}
                  className="group flex items-center gap-4 border-t border-border py-4 hover:text-accent transition-colors"
                >
                  <Mail size={16} className="text-accent shrink-0" />
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Email</div>
                    <div className="text-base md:text-lg">{CONTENT.contact.email}</div>
                  </div>
                  <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <a
                  data-testid="contact-info-phone"
                  href={`tel:${CONTENT.contact.phone.replace(/\s/g, "")}`}
                  className="group flex items-center gap-4 border-t border-border py-4 hover:text-accent transition-colors"
                >
                  <Phone size={16} className="text-accent shrink-0" />
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Phone</div>
                    <div className="text-base md:text-lg">{CONTENT.contact.phone}</div>
                  </div>
                  <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <a
                  data-testid="contact-info-linkedin"
                  href={CONTENT.contact.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 border-t border-b border-border py-4 hover:text-accent transition-colors"
                >
                  <Linkedin size={16} className="text-accent shrink-0" />
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">LinkedIn</div>
                    <div className="text-base md:text-lg">{CONTENT.contact.linkedin}</div>
                  </div>
                  <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            </motion.aside>

            <motion.form
              onSubmit={submit}
              noValidate
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-7 space-y-8 relative"
              data-testid="contact-form"
              aria-busy={submitting}
            >
              {status === "success" ? (
                <div data-testid="contact-success" className="border border-accent/60 bg-accent/10 p-8 md:p-10 space-y-4" role="status">
                  <div className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-accent">
                    <Check size={14} /> Message delivered
                  </div>
                  <p className="text-base md:text-lg text-foreground/85">Thanks — I read every message and reply within 24 hours (IST).</p>
                  <button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={() => setStatus("idle")}
                    className="inline-flex items-center gap-2 border border-border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <Field label="Name" code="01" testId="contact-input-name" value={form.name} onChange={change("name")} onBlur={blur("name")} error={touched.name && errors.name} autoComplete="name" disabled={submitting} />
                    <Field label="Email" code="02" type="email" testId="contact-input-email" value={form.email} onChange={change("email")} onBlur={blur("email")} error={touched.email && errors.email} autoComplete="email" disabled={submitting} />
                  </div>
                  <Field label="Company (optional)" code="03" testId="contact-input-company" value={form.company} onChange={change("company")} autoComplete="organization" disabled={submitting} />
                  <div>
                    <div className="flex items-baseline gap-3 mb-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent">04 /</span>
                      <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Message
                      </label>
                    </div>
                    <textarea
                      id="contact-message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={change("message")}
                      onBlur={blur("message")}
                      rows={5}
                      disabled={submitting}
                      aria-invalid={!!(touched.message && errors.message)}
                      aria-describedby={touched.message && errors.message ? "contact-message-error" : undefined}
                      className={`w-full bg-transparent border-b focus:border-accent focus:outline-none py-3 font-sans text-base md:text-lg resize-none transition-colors ${
                        touched.message && errors.message ? "border-destructive" : "border-border"
                      }`}
                      placeholder="Role, team, timeline..."
                    />
                    {touched.message && errors.message && (
                      <p id="contact-message-error" data-testid="contact-error-message" className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-destructive">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {/* Honeypot — hidden from humans, tempting to bots */}
                  <div className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="contact-website">Website</label>
                    <input id="contact-website" data-testid="contact-honeypot" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={change("website")} />
                  </div>

                  {status === "error" && (
                    <div data-testid="contact-error-banner" role="alert" className="border border-destructive/60 bg-destructive/10 p-4 flex flex-wrap items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-2 text-sm text-foreground/90">
                        <AlertTriangle size={14} className="text-destructive" /> {serverError}
                      </span>
                      <a href={`mailto:${CONTENT.contact.email}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent hover:text-foreground">
                        Email directly →
                      </a>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Reply within 24h · IST</div>
                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={submitting}
                      className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? <Loader2 size={14} className="animate-spin" /> : status === "error" ? <RefreshCw size={14} /> : <ArrowRight size={14} />}
                      {submitting ? "Sending" : status === "error" ? "Retry" : "Send message"}
                    </button>
                  </div>
                </>
              )}
            </motion.form>
          </div>
        </ChapterHeader>
      </div>
    </section>
  );
}

function Field({ label, code, value, onChange, onBlur, type = "text", testId, error, autoComplete, disabled }) {
  const id = `contact-${testId}`;
  return (
    <div>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent">{code} /</span>
        <label htmlFor={id} className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </label>
      </div>
      <input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`w-full bg-transparent border-b focus:border-accent focus:outline-none py-3 font-sans text-base md:text-lg transition-colors ${
          error ? "border-destructive" : "border-border"
        }`}
      />
      {error && (
        <p id={`${id}-error`} data-testid={`${testId}-error`} className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
